type PhoneNumberResult = {
  number: string;
  isValid: () => boolean;
};

type LibPhoneNumberModule = {
  parsePhoneNumberFromString: (
    phone: string,
    countryCode?: string,
  ) => PhoneNumberResult | undefined;
};

type WindowWithLibPhoneNumber = {
  libphonenumber?: LibPhoneNumberModule;
};

const getLibPhoneNumber = (): LibPhoneNumberModule | undefined =>
  (window as unknown as WindowWithLibPhoneNumber).libphonenumber;

const PHONE_LIB_TIMEOUT_MS = 300;

let libPhoneNumberReady: Promise<boolean> | null = null;

const loadLibPhoneNumber = (): Promise<boolean> => {
  if (libPhoneNumberReady) return libPhoneNumberReady;
  libPhoneNumberReady = new Promise((resolve) => {
    if (getLibPhoneNumber()) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/libphonenumber-js@1.13.12/bundle/libphonenumber-min.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
  return libPhoneNumberReady;
};

const withTimeout = <T>(promise: Promise<T>, ms: number, fallbackValue: T): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallbackValue), ms)),
  ]);

const memoCache = new Map<string, Promise<string | undefined>>();

export const normalisePhoneE164 = (
  rawPhone?: string,
  isoCountryCode?: string,
): Promise<string | undefined> => {
  if (!rawPhone) return Promise.resolve(rawPhone);
  const cacheKey = `${rawPhone}|${isoCountryCode ?? ''}`;
  const cached = memoCache.get(cacheKey);
  if (cached) return cached;
  const result = (async () => {
    const loaded = await withTimeout(loadLibPhoneNumber(), PHONE_LIB_TIMEOUT_MS, false);
    const lib = getLibPhoneNumber();
    if (!loaded || !lib) return rawPhone;
    try {
      const parsed = lib.parsePhoneNumberFromString(rawPhone, isoCountryCode);
      return parsed && parsed.isValid() ? parsed.number : rawPhone;
    } catch {
      return rawPhone;
    }
  })();

  memoCache.set(cacheKey, result);
  return result;
};
